<template>
    <div v-if="visible" class="sm-overlay" @click.self="$emit('close')">
        <div class="sm-modal">
            <div class="sm-header">
                <h3 class="sm-title">LLM Configuration</h3>
                <button class="sm-close" @click="$emit('close')">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>

            <div class="sm-body">
                <div class="sm-field">
                    <label class="sm-label">API Base URL</label>
                    <select class="sm-select" v-model="form.llm_api_base">
                        <option value="https://openrouter.ai/api/v1">OpenRouter (Free Models)</option>
                        <option value="https://open.bigmodel.cn/api/paas/v4">GLM (Zhipu)</option>
                        <option value="https://api.deepseek.com/v1">DeepSeek</option>
                        <option value="https://api.openai.com/v1">OpenAI</option>
                        <option value="custom">Custom...</option>
                    </select>
                    <input
                        v-if="form.llm_api_base === 'custom'"
                        type="text"
                        class="sm-input"
                        v-model="customBase"
                        placeholder="https://your-api.example.com/v1"
                    />
                </div>

                <div class="sm-field">
                    <label class="sm-label">API Key</label>
                    <input
                        type="password"
                        class="sm-input"
                        v-model="form.llm_api_key"
                        :placeholder="keyPlaceholder"
                    />
                    <span class="sm-hint">Leave empty to keep current key</span>
                </div>

                <div class="sm-field">
                    <label class="sm-label">Model</label>
                    <select class="sm-select" v-model="form.llm_model">
                        <option value="openai/gpt-oss-120b:free">GPT OSS 120B (Free)</option>
                        <option value="nvidia/nemotron-3-super-120b-a12b:free">Nemotron 3 Super 120B (Free)</option>
                        <option value="poolside/laguna-m.1:free">Laguna M.1 (Free)</option>
                        <option value="z-ai/glm-4.5-air:free">GLM 4.5 Air (Free)</option>
                        <option value="deepseek/deepseek-v4-flash:free">DeepSeek V4 Flash (Free)</option>
                        <option value="meta-llama/llama-3.3-70b-instruct:free">Llama 3.3 70B (Free)</option>
                        <option value="google/gemma-4-31b-it:free">Gemma 4 31B (Free)</option>
                        <option value="qwen/qwen3-next-80b-a3b-instruct:free">Qwen3 Next 80B (Free)</option>
                        <option value="glm-4-flash">GLM-4-Flash</option>
                        <option value="deepseek-chat">DeepSeek Chat</option>
                    </select>
                </div>
            </div>

            <div class="sm-footer">
                <span v-if="message" class="sm-message" :class="{ 'sm-message--error': isError }">{{ message }}</span>
                <div class="sm-actions">
                    <button class="sm-btn sm-btn--secondary" @click="$emit('close')">Cancel</button>
                    <button class="sm-btn sm-btn--primary" @click="save" :disabled="saving">
                        {{ saving ? 'Saving...' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getToken, clearAuth } from '../../utils/auth';

const API_BASE = 'https://economic-news-crawler.gson.workers.dev';

export default {
    name: 'SettingsModal',
    props: {
        visible: { type: Boolean, default: false },
    },
    emits: ['close', 'saved'],
    data() {
        return {
            form: {
                llm_api_base: 'https://openrouter.ai/api/v1',
                llm_api_key: '',
                llm_model: 'nvidia/nemotron-3-super-120b-a12b:free',
            },
            customBase: '',
            keyPlaceholder: '',
            saving: false,
            message: '',
            isError: false,
        };
    },
    watch: {
        visible(v) {
            if (v) this.loadConfig();
        },
    },
    methods: {
        async loadConfig() {
            this.message = '';
            this.showKey = false;
            try {
                const token = getToken();
                const res = await fetch(`${API_BASE}/config/llm`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    this.form.llm_api_base = data.llm_api_base || 'https://open.bigmodel.cn/api/paas/v4';
                    this.form.llm_model = data.llm_model || 'glm-4-flash';
                    this.keyPlaceholder = data.llm_api_key ? `Current: ${data.llm_api_key}` : 'Enter API key';
                }
            } catch {
                // ignore
            }
        },
        async save() {
            this.saving = true;
            this.message = '';
            this.isError = false;

            try {
                const token = getToken();
                const payload = {
                    llm_api_base: this.form.llm_api_base === 'custom' ? this.customBase : this.form.llm_api_base,
                    llm_model: this.form.llm_model,
                };
                if (this.form.llm_api_key) {
                    payload.llm_api_key = this.form.llm_api_key;
                }

                const res = await fetch(`${API_BASE}/config/llm`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

                const data = await res.json();
                if (res.status === 401) {
                    clearAuth();
                    this.$emit('close');
                    this.$router.push('/userLogin');
                    return;
                }
                if (res.ok && data.success) {
                    this.message = 'Saved successfully';
                    this.form.llm_api_key = '';
                    this.$emit('saved');
                } else {
                    this.isError = true;
                    this.message = data.error || 'Save failed';
                }
            } catch (e) {
                this.isError = true;
                this.message = 'Network error';
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.sm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.sm-modal {
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    width: 440px;
    max-width: 90vw;
    border: 1px solid var(--border-light);
}

.sm-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 0;
}

.sm-title {
    font-family: var(--font-heading);
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
}

.sm-close {
    background: none;
    border: none;
    color: var(--text-disabled);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color var(--transition-fast);
}

.sm-close:hover {
    color: var(--text-secondary);
}

.sm-body {
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.sm-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sm-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.sm-input, .sm-select {
    font-family: var(--font-body);
    font-size: 13px;
    padding: 9px 12px;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-secondary);
    color: var(--text-primary);
    transition: all var(--transition-fast);
    appearance: none;
}

.sm-select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 30px;
}

.sm-input:focus, .sm-select:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.sm-hint {
    font-size: 11px;
    color: var(--text-disabled);
}

.sm-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-top: 1px solid var(--border-light);
}

.sm-message {
    font-size: 12px;
    color: #22c55e;
}

.sm-message--error {
    color: #ef4444;
}

.sm-actions {
    display: flex;
    gap: 8px;
}

.sm-btn {
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-fast);
    border: 1px solid var(--border-color);
}

.sm-btn--secondary {
    background: var(--bg-secondary);
    color: var(--text-secondary);
}

.sm-btn--secondary:hover {
    border-color: var(--text-tertiary);
}

.sm-btn--primary {
    background: var(--color-primary);
    color: var(--text-inverse);
    border-color: var(--color-primary);
}

.sm-btn--primary:hover {
    opacity: 0.9;
}

.sm-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
